/**
 * Safe-area insets probe. iOS Safari + standalone PWAs need the
 * env(safe-area-inset-*) padding values in JS (Pixi can't read them
 * directly). We mount a hidden DOM element with those env() values as
 * padding and read computed styles back. Idempotent: returns the same
 * <div> across calls.
 */

export interface SafeInsets {
  top: number; right: number; bottom: number; left: number;
}

export class SafeAreaProbe {
  private el: HTMLDivElement | null = null;

  /** Read the current insets in CSS pixels. Cheap; safe to call every
   *  resize / per-frame. */
  read(): SafeInsets {
    const probe = this.el ?? this.create();
    const cs = getComputedStyle(probe);
    return {
      top:    parseFloat(cs.paddingTop)    || 0,
      right:  parseFloat(cs.paddingRight)  || 0,
      bottom: parseFloat(cs.paddingBottom) || 0,
      left:   parseFloat(cs.paddingLeft)   || 0,
    };
  }

  /** Remove the probe element from the document. Idempotent. */
  destroy(): void {
    if (this.el) {
      try { this.el.remove(); } catch { /* ignore */ }
      this.el = null;
    }
  }

  private create(): HTMLDivElement {
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;'
      + 'padding-top:env(safe-area-inset-top);'
      + 'padding-right:env(safe-area-inset-right);'
      + 'padding-bottom:env(safe-area-inset-bottom);'
      + 'padding-left:env(safe-area-inset-left);';
    document.body.appendChild(el);
    this.el = el;
    return el;
  }
}
